import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button"
import {
  Dialog, DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { Calendar } from "../ui/calendar"
import { CalendarIcon } from "lucide-react"
import { format, endOfYear, parseISO, isAfter, isBefore } from "date-fns"
import { cn } from "../../lib/utils"
import apiClient from '../../services/api';
import { Goal, Category } from '../../types';
import { toast } from "../../hooks/use-toast";
import { Switch } from "../ui/switch";
import { HierarchicalGoalSelect } from './HierarchicalGoalSelect';

// Interface for props including existing goal for editing
interface GoalFormProps {
  goal?: Goal | null; // Optional: Pass existing goal to edit
  categories: Category[];
  parentGoals: Goal[]; // For parent goal selection
  refreshGoals: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  clearGoalToEdit: () => void;
}

const GoalForm: React.FC<GoalFormProps> = ({ goal, categories, parentGoals, refreshGoals, isOpen, setIsOpen, clearGoalToEdit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [parentGoalId, setParentGoalId] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [deadline, setDeadline] = useState<Date | undefined>(endOfYear(new Date()));
  const [priority, setPriority] = useState<string>('medium');
  const [completedAt, setCompletedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parentDeadline, setParentDeadline] = useState<Date | null>(null);

  const isEditing = !!goal;
  const isCompleted = completedAt !== null;

  useEffect(() => {
    if (isOpen) {
      setParentDeadline(null);
      if (isEditing && goal) {
        setTitle(goal.title);
        setDescription(goal.description || '');
        setCategoryId(goal.category?.toString() || undefined);
        setParentGoalId(goal.parent_goal?.toString() || undefined);
        setStartDate(goal.start_date ? new Date(goal.start_date) : undefined);
        setDeadline(goal.deadline ? new Date(goal.deadline) : undefined);
        setPriority(goal.priority || 'medium');
        setCompletedAt(goal.completed_at ? new Date(goal.completed_at) : null);
        setError(null);
      } else {
        setTitle('');
        setDescription('');
        setCategoryId(undefined);
        setParentGoalId(undefined);
        setStartDate(new Date());
        setDeadline(endOfYear(new Date()));
        setPriority('medium');
        setCompletedAt(null);
        setError(null);
      }
    }
  }, [goal, isEditing, isOpen]);

  useEffect(() => {
    if (parentGoalId) {
      const selectedParent = parentGoals.find(p => p.id.toString() === parentGoalId);
      if (selectedParent) {
        const pDeadline = selectedParent.deadline ? parseISO(selectedParent.deadline) : null;
        setParentDeadline(pDeadline);

        if (!isEditing) {
          if (selectedParent.category) {
            setCategoryId(selectedParent.category.toString());
          }
          if (selectedParent.deadline) {
            setDeadline(new Date(selectedParent.deadline));
          } else {
            setDeadline(undefined);
          }
        }
      } else {
        setParentDeadline(null);
      }
    } else {
      setParentDeadline(null);
    }
  }, [parentGoalId, parentGoals, isEditing]);

  const handleClose = () => {
    setIsOpen(false);
    clearGoalToEdit();
  };

  const handleToggleCompletion = async () => {
    if (!isEditing || !goal) return;
    
    const newCompletedAtValue = isCompleted ? null : new Date().toISOString();
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.patch(`/goals/goals/${goal.id}/`, { completed_at: newCompletedAtValue });
      setCompletedAt(newCompletedAtValue ? new Date(newCompletedAtValue) : null);
      refreshGoals();
      toast({ title: "Goal status updated!" });
    } catch (err: any) {
      console.error("Failed to update goal status:", err);
      let errorMessage = "Failed to update goal status.";
      let errorDetail = null;
      if (err.response?.status === 400 && err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
        if (errorMessage.startsWith("Cannot complete this goal")) {
           try {
             const blockingGoals = goal.sub_goals?.filter((sg: Goal) => !sg.completed_at).map((sg: Goal) => sg.title) ?? [];
             if (blockingGoals.length > 0) {
                 errorDetail = `Incomplete sub-goals: ${blockingGoals.join(", ")}`;
             } else {
                errorDetail = "Please ensure all sub-goals are completed first.";
             }
             errorMessage = "Cannot complete goal";
           } catch (parseError) { 
               console.error("Error parsing sub-goal error:", parseError);
               errorDetail = "Please ensure all sub-goals are completed first.";
               errorMessage = "Cannot complete goal";
           } 
        } 
      } else if (err.response?.data) {
        errorMessage = `Update failed: ${JSON.stringify(err.response.data)}`;
      }
      toast({ 
        title: errorMessage,
        description: errorDetail,
        variant: "destructive",
        duration: 5000 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const payload = {
      title,
      description,
      category: categoryId ? parseInt(categoryId) : null,
      parent_goal: parentGoalId ? parseInt(parentGoalId) : null,
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
      deadline: deadline ? format(deadline, 'yyyy-MM-dd') : null,
      priority,
    };

    try {
      if (isEditing && goal) {
        await apiClient.put(`/goals/goals/${goal.id}/`, payload);
      } else {
        await apiClient.post('/goals/goals/', payload);
      }
      refreshGoals();
      handleClose();
      toast({ title: isEditing ? "Goal updated!" : "Goal created!" });
    } catch (err: any) {
      console.error("Failed to save goal:", err);
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save goal.';
      if (errorData) {
        errorMessage = Object.entries(errorData)
          .map(([key, value]) => {
            const fieldName = key.replace('_', ' ').replace(/^./, str => str.toUpperCase());
            return `${fieldName}: ${(Array.isArray(value) ? value.join(', ') : value)}`;
           })
          .join('; ');
      }
      toast({ 
        title: "Save Failed",
        description: errorMessage,
        variant: "destructive",
        duration: 5000 
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGoal = async () => {
    if (!isEditing || !goal) return;
    if (!window.confirm("Are you sure you want to delete this goal and all its sub-goals and tasks?")) return;
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.delete(`/goals/goals/${goal.id}/`);
      refreshGoals();
      handleClose();
      toast({ title: "Goal deleted" });
    } catch (err: any) { 
      console.error("Failed to delete goal:", err);
      const errMsg = err.response?.data?.detail || err.message || 'Failed to delete goal.';
      toast({ title: "Delete Failed", description: errMsg, variant: "destructive" });
      setError(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeadlineSelect = (selectedDate: Date | undefined) => {
    setDeadline(selectedDate);
    if (selectedDate && parentDeadline && isAfter(selectedDate, parentDeadline)) {
      toast({
        title: "Warning: Deadline is after parent goal's deadline",
        description: `Parent deadline: ${format(parentDeadline, "PPP")}. You can proceed, but consider aligning dates.`,
        variant: "default",
        duration: 5000,
      });
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {if (!open) handleClose(); else setIsOpen(open);}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Goal' : 'Add New Goal'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the details of your goal.' : 'Create a new goal.'} Click save when done.
          </DialogDescription>
        </DialogHeader>
        {error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm mb-4">
                {error}
            </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal-title" className="text-right">Title</Label>
              <Input 
                id="goal-title" 
                value={title} 
                onChange={(e) => setTitle(e.target.value)}
                className="col-span-3" 
                required
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="goal-description" className="text-right pt-2">Description</Label>
              <Textarea 
                id="goal-description" 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="(Optional) Add details..."
                className="col-span-3"
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal-category" className="text-right">Category</Label>
              <Select value={categoryId || ''} onValueChange={(value) => setCategoryId(value === 'null' ? undefined : value)} disabled={isLoading}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="(Optional) Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="null">(No Category)</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal-parent" className="text-right">Parent Goal</Label>
              <HierarchicalGoalSelect
                  className="col-span-3"
                  goals={parentGoals}
                  categories={categories}
                  value={parentGoalId ? parseInt(parentGoalId) : null}
                  onChange={(selectedId) => setParentGoalId(selectedId?.toString())}
                  placeholder="(Optional) Select parent"
                  allowClear={true}
                  disabled={isLoading}
                  filterActiveOnly={true}
                  maxDepth={5}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal-priority" className="text-right">Priority</Label>
              <Select value={priority || 'medium'} onValueChange={setPriority} disabled={isLoading}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal-start-date" className="text-right">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "col-span-3 justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                    disabled={isLoading}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : <span>(Optional) Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    disabled={(date) => deadline ? date > deadline : false}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal-deadline" className="text-right">Deadline</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "col-span-3 justify-start text-left font-normal",
                      !deadline && "text-muted-foreground"
                    )}
                    disabled={isLoading}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {deadline ? format(deadline, "PPP") : <span>(Optional) Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={deadline}
                    onSelect={handleDeadlineSelect}
                    disabled={(date) => 
                        (startDate ? isBefore(date, startDate) : false) || 
                        (parentDeadline ? isAfter(date, parentDeadline) : false)
                    }
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Completion Status Switch (only for existing goals) */} 
            {isEditing && (
              <div className="grid grid-cols-4 items-center gap-4">
                 <Label htmlFor="goal-completion-status" className="text-right">Status</Label>
                 <div className="col-span-3 flex items-center space-x-2">
                    <Switch
                        id="goal-completion-status"
                        checked={isCompleted}
                        onCheckedChange={handleToggleCompletion}
                        disabled={isLoading}
                        aria-label={isCompleted ? "Mark goal as active" : "Mark goal as complete"}
                    />
                    <span className="text-sm text-muted-foreground">
                        {isCompleted ? "Completed" : "Active"}
                    </span>
                 </div>
              </div>
            )}
          </div>

          {/* Footer without completion button */} 
          <DialogFooter className="sm:justify-end gap-2 mt-4"> 
            <div className="flex gap-2 justify-end">
                {isEditing && (
                 <Button type="button" variant="destructive" onClick={handleDeleteGoal} disabled={isLoading}>
                     Delete Goal
                  </Button>
                )}
                <DialogClose asChild>
                    <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
                </DialogClose>
                <Button type="submit" disabled={isLoading}>{isLoading ? 'Saving...' : (isEditing ? 'Update Goal' : 'Save Goal')}</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GoalForm; 